import { useState } from 'react';
import { LocationOnIcon, Text } from '@billda/design-system';
import ConfirmDialog from '../ui/ConfirmDialog';

interface What3WordsLinkProps {
  address: string;
  /** Whether to show a confirmation dialog before opening the link. Default: true */
  requireConfirmation?: boolean;
  /** Text variant for the address text. Default: 'body2' */
  variant?: 'body2' | 'caption';
  /** Test ID for the link element */
  testId?: string;
}

/**
 * A reusable link component for What3Words addresses.
 * Shows the location icon followed by the address text.
 * Optionally shows a confirmation dialog before opening the external link.
 */
export function What3WordsLink({
  address,
  requireConfirmation = true,
  variant = 'body2',
  testId = 'what3words-link',
}: What3WordsLinkProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const getWhat3WordsUrl = (addr: string) => {
    // Remove the /// prefix if present
    const cleanAddress = addr.replace(/^\/+/, '');
    return `https://what3words.com/${cleanAddress}`;
  };

  const url = getWhat3WordsUrl(address);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (requireConfirmation) {
      setConfirmDialogOpen(true);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleConfirm = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={testId}
        className="flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-left text-content-accent underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-border-focused"
      >
        <LocationOnIcon aria-hidden="true" className="size-4" />
        <Text as="span" variant={variant}>
          {address}
        </Text>
      </button>

      {requireConfirmation && (
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Open External Location"
          message="This will open What3Words in a new tab. Do you want to continue?"
          confirmText="Open"
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}
